// src/pages/RefundRequestPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { API_CONFIG } from '../utils/apiConfig';
import { showToast } from "../utils/helpers";
import HeaderLanding from "../components/partials/HeaderLanding";
import SmartsuppChat from "../components/external/SmartsuppChat";

const RefundRequestPage = () => {
  const { orderId: urlOrderId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    orderId: urlOrderId || "",
    refundType: "",
    reason: "",
    description: "",
    amount: "",
    evidenceUrls: [],
    contactEmail: "",
    contactPhone: "",
    preferredContact: "email"
  });

  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  // If order ID is provided in URL, fetch order data automatically
  useEffect(() => {
    if (urlOrderId) {
      setFormData(prev => ({ ...prev, orderId: urlOrderId }));
      fetchOrderData(urlOrderId);
    }
  }, [urlOrderId]);

  const fetchOrderData = async (id) => {
    if (!id.trim()) {
      setError("Please enter an order ID");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await api.get(`/api/orders/track/${id}`);

      if (response.data.success) {
        const order = response.data.order;
        setOrderData(order);
        
        // Pre-fill form with order data
        setFormData(prev => ({
          ...prev,
          contactEmail: order.customer_email || "",
          contactPhone: order.customer_phone || "",
          amount: order.total_amount || ""
        }));
        
        if (id !== urlOrderId) {
          navigate(`/request-refund/${id}`, { replace: true });
        }
      } else {
        setError(response.data.message || "Order not found");
        setOrderData(null);
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      setError(error.response?.data?.message || "Failed to fetch order details");
      setOrderData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const uploadFile = async (file, fileIndex) => {
    const abortController = new AbortController();
    
    setUploadingFiles(prev => ({
      ...prev,
      [fileIndex]: {
        file,
        abortController,
        status: 'uploading'
      }
    }));

    setUploadProgress(prev => ({
      ...prev,
      [fileIndex]: 0
    }));

    setUploadErrors(prev => ({
      ...prev,
      [fileIndex]: null
    }));

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('uploadType', 'refund_evidence');

      const uploadResponse = await api.post(API_CONFIG.PRODUCTS.UPLOAD, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
        signal: abortController.signal,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const uploadPercentage = (progressEvent.loaded / progressEvent.total) * 100;
            setUploadProgress(prev => ({
              ...prev,
              [fileIndex]: Math.min(uploadPercentage, 100)
            }));
          } else {
            setUploadProgress(prev => ({
              ...prev,
              [fileIndex]: Math.min((prev[fileIndex] || 0) + 10, 90)
            }));
          }
        },
      });

      if (uploadResponse.data.success && uploadResponse.data.url) {
        setFormData(prev => ({
          ...prev,
          evidenceUrls: [...prev.evidenceUrls, {
            url: uploadResponse.data.url,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size
          }]
        }));

        setUploadingFiles(prev => ({
          ...prev,
          [fileIndex]: {
            ...prev[fileIndex],
            status: 'completed'
          }
        }));

        setUploadProgress(prev => ({
          ...prev,
          [fileIndex]: 100
        }));

        return uploadResponse.data.url;
      } else {
        throw new Error(uploadResponse.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to upload file';

      setUploadErrors(prev => ({
        ...prev,
        [fileIndex]: errorMessage
      }));

      setUploadingFiles(prev => ({
        ...prev,
        [fileIndex]: {
          ...prev[fileIndex],
          status: 'error'
        }
      }));

      throw error;
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      const maxSize = 10 * 1024 * 1024;
      const allowedTypes = [
        'image/jpeg', 
        'image/png', 
        'image/jpg', 
        'application/pdf', 
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      if (file.size > maxSize) {
        setUploadErrors(prev => ({
          ...prev,
          [file.name]: 'File size must be less than 10MB'
        }));
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        setUploadErrors(prev => ({
          ...prev,
          [file.name]: 'File type not supported. Please upload images, PDF, or Word documents'
        }));
        return false;
      }

      return true;
    });

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const fileIndex = `${file.name}-${Date.now()}-${i}`;
      
      try {
        await uploadFile(file, fileIndex);
      } catch (error) {
        continue;
      }
    }
  };

  const cancelUpload = (fileIndex) => {
    const uploadInfo = uploadingFiles[fileIndex];
    if (uploadInfo && uploadInfo.abortController) {
      uploadInfo.abortController.abort();
    }

    setUploadingFiles(prev => {
      const newUploading = { ...prev };
      delete newUploading[fileIndex];
      return newUploading;
    });

    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileIndex];
      return newProgress;
    });

    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileIndex];
      return newErrors;
    });
  };

  const removeUploadedFile = (index) => {
    setFormData(prev => ({
      ...prev,
      evidenceUrls: prev.evidenceUrls.filter((_, i) => i !== index)
    }));
  };

  const isFormValid = () => {
    return (
      formData.orderId.trim() &&
      formData.refundType &&
      formData.reason &&
      formData.description.trim() &&
      formData.amount &&
      formData.contactEmail &&
      orderData
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setError("Please complete all required fields and wait for file uploads to finish");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.post(API_CONFIG.REFUNDS.REQUEST_REFUND, {
        ...formData,
        evidence: formData.evidenceUrls
      });

      if (response.data.success) {
        setSuccess("Refund request submitted successfully! Your case reference is: " + response.data.caseId);
        setFormData({
          orderId: "",
          refundType: "",
          reason: "",
          description: "",
          amount: "",
          evidenceUrls: [],
          contactEmail: "",
          contactPhone: "",
          preferredContact: "email"
        });
        setOrderData(null);
        setUploadProgress({});
        setUploadingFiles({});
        setUploadErrors({});
        
        setTimeout(() => {
          navigate('/refund-status/' + response.data.caseId);
        }, 5000);
      } else {
        setError(response.data.message || "Failed to submit refund request");
      }
    } catch (error) {
      console.error("Error submitting refund request:", error);
      setError(error.response?.data?.message || "Failed to submit refund request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const refundTypes = [
    { value: "full", label: "Full Refund", description: "Complete refund of the order amount" },
    { value: "partial", label: "Partial Refund", description: "Refund for specific items or partial amount" },
    { value: "shipping", label: "Shipping Refund", description: "Refund for shipping costs only" }
  ];

  const refundReasons = [
    { value: "faulty_product", label: "Faulty or Defective Product", category: "product_issue" },
    { value: "wrong_item", label: "Wrong Item Delivered", category: "delivery_issue" },
    { value: "not_as_described", label: "Item Not as Described", category: "product_issue" },
    { value: "damaged", label: "Item Arrived Damaged", category: "delivery_issue" },
    { value: "missing_parts", label: "Missing Parts or Components", category: "product_issue" },
    { value: "non_delivery", label: "Item Never Delivered", category: "delivery_issue" },
    { value: "late_delivery", label: "Significant Delivery Delay", category: "delivery_issue" },
    { value: "service_not_provided", label: "Service Not Provided", category: "service_issue" },
    { value: "poor_service_quality", label: "Poor Service Quality", category: "service_issue" },
    { value: "unauthorized_purchase", label: "Unauthorized Purchase", category: "payment_issue" },
    { value: "duplicate_charge", label: "Duplicate Charge", category: "payment_issue" },
    { value: "other", label: "Other Reason", category: "other" }
  ];

  const formatCurrency = (amount, currency = 'NGN') => {
    if (!amount) return currency === 'NGN' ? '₦0.00' : '$0.00';
    return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-white w-full">
      <HeaderLanding />

      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold  mb-2 sm:mb-3">
            Request a Refund
          </h1>
          <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto px-2">
            Submit a refund request for your order. Our team will review your case and get back to you within 24-48 hours.
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-green-700 text-sm sm:text-base">{success}</p>
                <p className="text-green-600 text-xs sm:text-sm mt-1">
                  You will be redirected to your case status page shortly...
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Order ID Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order ID *
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      name="orderId"
                      value={formData.orderId}
                      onChange={handleInputChange}
                      placeholder="Enter your order ID (e.g., order_123456)"
                      className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      disabled={isLoading || isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => fetchOrderData(formData.orderId)}
                      disabled={isLoading || isSubmitting}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base whitespace-nowrap"
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="hidden sm:inline">Searching...</span>
                          <span className="sm:hidden">Search...</span>
                        </>
                      ) : (
                        "Find Order"
                      )}
                    </button>
                  </div>
                  {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                  )}
                </div>

                {/* Order Preview */}
                {orderData && (
                  <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-green-800 text-sm sm:text-base truncate">
                          Order #{orderData.order_id} Found
                        </h4>
                        <p className="text-green-700 text-xs sm:text-sm truncate">
                          Amount: {formatCurrency(orderData.total_amount)} • 
                          Status: {orderData.order_status}
                        </p>
                      </div>
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Refund Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Refund Type *
                  </label>
                  <div className="grid gap-2 sm:gap-3">
                    {refundTypes.map((type) => (
                      <label key={type.value} className="flex items-start p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-white cursor-pointer">
                        <input
                          type="radio"
                          name="refundType"
                          value={type.value}
                          checked={formData.refundType === type.value}
                          onChange={handleInputChange}
                          className="mt-1 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                        />
                        <div className="ml-3 min-w-0">
                          <span className="font-medium text-gray-900 text-sm sm:text-base block">{type.label}</span>
                          <p className="text-gray-500 text-xs sm:text-sm mt-1">{type.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Refund Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Refund *
                  </label>
                  <select
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  >
                    <option value="">Select a reason</option>
                    {refundReasons.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Detailed Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Please provide a detailed description of the issue. Include information about what went wrong, when it happened, and how it affected you..."
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base resize-vertical"
                  />
                </div>

                {/* Refund Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requested Refund Amount *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₦</span>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Evidence Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Supporting Evidence
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      accept="image/*,.pdf,.doc,.docx"
                      className="hidden"
                      id="evidence-upload"
                      disabled={Object.keys(uploadingFiles).length > 0}
                    />
                    <label 
                      htmlFor="evidence-upload" 
                      className={`cursor-pointer block ${Object.keys(uploadingFiles).length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2 sm:mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-gray-600 text-sm sm:text-base mb-1 sm:mb-2">
                        <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-gray-500 text-xs sm:text-sm">
                        PNG, JPG, PDF, DOC up to 10MB each
                      </p>
                    </label>
                  </div>

                  {/* Uploading Files */}
                  {Object.keys(uploadingFiles).length > 0 && (
                    <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                      <p className="text-sm font-medium text-gray-700">Uploading files...</p>
                      {Object.entries(uploadingFiles).map(([fileIndex, uploadInfo]) => (
                        <div key={fileIndex} className="p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between mb-1 sm:mb-2">
                            <div className="flex items-center min-w-0 flex-1">
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-xs sm:text-sm text-gray-700 truncate">{uploadInfo.file.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => cancelUpload(fileIndex)}
                              className="text-red-500 hover:text-red-700 text-xs sm:text-sm flex-shrink-0 ml-2"
                            >
                              Cancel
                            </button>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                            <div 
                              className="bg-blue-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress[fileIndex] || 0}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {uploadProgress[fileIndex]?.toFixed(0) || 0}% uploaded
                          </p>

                          {/* Upload Error */}
                          {uploadErrors[fileIndex] && (
                            <p className="text-xs text-red-600 mt-1 truncate">{uploadErrors[fileIndex]}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Uploaded Files */}
                  {formData.evidenceUrls.length > 0 && (
                    <div className="mt-3 sm:mt-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Uploaded files:</p>
                      {formData.evidenceUrls.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 sm:p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center min-w-0 flex-1">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <span className="text-xs sm:text-sm text-gray-700 block truncate">{file.fileName}</span>
                              <span className="text-xs text-gray-500">{formatFileSize(file.fileSize)}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeUploadedFile(index)}
                            className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Preferred Contact Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Preferred Contact Method
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="email"
                        checked={formData.preferredContact === "email"}
                        onChange={handleInputChange}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-gray-700 text-sm sm:text-base">Email</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="phone"
                        checked={formData.preferredContact === "phone"}
                        onChange={handleInputChange}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-gray-700 text-sm sm:text-base">Phone</span>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isFormValid() || isSubmitting}
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold text-base sm:text-lg transition-colors duration-200"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="hidden sm:inline">Submitting Request...</span>
                      <span className="sm:hidden">Submitting...</span>
                    </>
                  ) : (
                    "Submit Refund Request"
                  )}
                </button>

                {/* Upload Warning */}
                {Object.keys(uploadingFiles).length > 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-700 text-sm flex items-center">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Please wait for all files to finish uploading before submitting
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Sidebar - Help Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 sticky top-4 sm:top-6 lg:top-24">
              <h3 className="font-semibold  text-lg sm:text-xl mb-3 sm:mb-4">Need Help?</h3>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="p-3 sm:p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 text-sm sm:text-base mb-1 sm:mb-2">Contact Support</h4>
                  <p className="text-blue-700 text-xs sm:text-sm">
                    Email: <a href="mailto:support@paymeo.co" className="underline break-all">support@paymeo.co</a>
                  </p>
                  <p className="text-blue-700 text-xs sm:text-sm mt-1">
                    Phone: <a href="tel:+2348000000000" className="underline">+234 800 000 0000</a>
                  </p>
                </div>

                <div className="p-3 sm:p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-800 text-sm sm:text-base mb-1 sm:mb-2">Processing Time</h4>
                  <p className="text-green-700 text-xs sm:text-sm">
                    Refund requests are typically processed within 24-48 hours. You'll receive email updates throughout the process.
                  </p>
                </div>

                <div className="p-3 sm:p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 text-sm sm:text-base mb-1 sm:mb-2">Required Information</h4>
                  <ul className="text-blue-700 text-xs sm:text-sm space-y-1">
                    <li>• Valid Order ID</li>
                    <li>• Detailed description of issue</li>
                    <li>• Supporting evidence (photos, documents)</li>
                    <li>• Contact information</li>
                  </ul>
                </div>

                <div className="p-3 sm:p-4 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-yellow-800 text-sm sm:text-base mb-1 sm:mb-2">Important Notes</h4>
                  <ul className="text-yellow-700 text-xs sm:text-sm space-y-1">
                    <li>• Refunds are processed to original payment method</li>
                    <li>• Processing may take 5-10 business days after approval</li>
                    <li>• Keep all original packaging until case is resolved</li>
                    <li>• Files are uploaded separately before form submission</li>
                  </ul>
                </div>
              </div>

              <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
                <a
                  href="/terms#refund-policy"
                  className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-medium"
                >
                  View Refund Policy →
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <SmartsuppChat /> 
      </div>
    </div>
  );
};

export default RefundRequestPage;